import { useLazyQuery, useMutation } from '@apollo/client';
import { CREATE_POSITION_TITLE, POSITIONS_BY_NAME_AND_CATEGORY, UPDATE_POSITION_TITLE } from '../../../graphql/position-gql';
import { ICreatePositionTitleResponse, IPositionsByNameAndCategoryResponse, IPositionTitleInfo, IPositionTitleInput, ISearchPositionTitlesInput, IUpdatePositionTitleResponse } from '../../../types/position';
import { Action } from '../../../types/reducer-action';
import { parseObject, toOrdered, trimToEmpty } from '../../../utils/commons';
import { DataEntryEvent } from '../../common/data-entry-reducer';
import { usePositionTitleDispatch } from './position-manager-context';
import { FetchPolicyOption } from '../../../services/apollo/apollo';

export const isSamePositionTitle = (a?: IPositionTitleInfo, b?: IPositionTitleInfo) => {
  if (!a && !b) return true;
  if (!a || !b) return false;

  const left = { ...a } as Record<string, any>;
  const right = { ...b } as Record<string, any>;

  return Object.keys(right)
    .filter(key => !['positionTitleId'].includes(key))
    .every(key => trimToEmpty(right[key]) === trimToEmpty(left[key]));
};

export const toPositionTitleInput = (form?: IPositionTitleInfo) => {
  if (!form) return undefined;

  const input = {
    categoryId: form.categoryId,
    positionName: form.positionName,
    status: form.status?.toString() === 'true'
  };

  return parseObject<IPositionTitleInput>(input);
}
interface PositionTitleApi {
  searchPositionTitles: (input: ISearchPositionTitlesInput) => void;
  createPositionTitle: (form: IPositionTitleInfo) => Promise<boolean>;
  updatePositionTitle: (positionTitleId: number, form: IPositionTitleInfo) => Promise<boolean>;
  busy: boolean;
}

export default function usePositionTitleApi(): PositionTitleApi {
  const dispatch = usePositionTitleDispatch();

  const [create, { loading: creating }] = useMutation<ICreatePositionTitleResponse>(CREATE_POSITION_TITLE);

  const [update, { loading: updating }] = useMutation<IUpdatePositionTitleResponse>(UPDATE_POSITION_TITLE);

  const [search, { loading: searching }] = useLazyQuery<IPositionsByNameAndCategoryResponse>(POSITIONS_BY_NAME_AND_CATEGORY, FetchPolicyOption.NoCache)

  const setResults = (results: IPositionTitleInfo[] | undefined) =>
    dispatch(new Action(DataEntryEvent.NewDataSet, results));

  const searchPositionTitles = (input: ISearchPositionTitlesInput) => {
    setResults(undefined);

    search({ variables: { input } })
      .then(response => {
        const results = toOrdered(response.data?.positionsByNameAndCategory,
          (item) => item.positionName.toLowerCase() === input.positionName.toLowerCase() ? -1 : 0
        );

        setResults(results);
      });
  };

  const createPositionTitle = async (form: IPositionTitleInfo) => {
    const input = toPositionTitleInput(form);
    return !!input && create({
      variables: {
        input: input
      }
    }).then(response => !!response.data?.createPositionTitle);
  };

  const updatePositionTitle = async (positionTitleId: number, form: IPositionTitleInfo) => {
    const input = toPositionTitleInput(form);
    return !!input && update({
      variables: {
        positionTitleId: positionTitleId,
        input: input
      }
    }).then(response => {
      const result = response.data?.updatePositionTitle;
      result && dispatch(new Action(DataEntryEvent.UpdateData, result));
      return !!result;
    });
  };

  const busy = searching || creating || updating;

  return {
    searchPositionTitles,
    createPositionTitle,
    updatePositionTitle,
    busy
  };
}
